import { Alert, Portal, Snackbar, Stack } from '@mui/material';

import GhostButton from '../../shared/button/GhostButton';
import PrimaryButton from '../../shared/button/PrimaryButton';
import { SiblingConfirmTarget } from './sibling_confirm';

interface SiblingConfirmBarProps {
  /** The label, metric and rows the offer applies to. */
  target: SiblingConfirmTarget;
  /** Whether the apply request is in flight (disables both actions). */
  busy: boolean;
  /** Applies the confirmed metric to every row in the target. */
  onApply: () => void;
  /** Dismisses the offer without changing anything. */
  onDismiss: () => void;
}

/**
 * The follow-up offer shown after a reviewer confirms one record's metric: the same
 * file holds other rows with that label which can take the same metric, and this
 * turns them into one click instead of one-by-one triage.
 *
 * DOCKED TO THE VIEWPORT, for three reasons that ruled out every placement inside
 * the list:
 * - The trigger disappears. Confirming pushes `metricConfidence` to 1.0, crossing
 *   the 0.9 threshold, so the row flips SUGGESTED → EXACT and drops straight out of
 *   the filtered set whenever the reviewer arrived via the "To confirm" chip — the
 *   most natural way to reach it. Anything anchored to that row goes with it.
 * - The siblings are not necessarily nearby. Grouped by entity or period they sit in
 *   other groups entirely, so a bar inside one group would claim a scope it lacks.
 * - Rendered above the list it pushed every group down on appear and back up on
 *   dismiss, the layout-shift problem the single notification slot exists to avoid.
 *
 * It uses MUI's Snackbar for positioning and transition only — with NO
 * `autoHideDuration`, because an action that edits records must never sit behind a
 * timer (the reason the shared FeedbackSnackbar, which hides after 6s, was rejected).
 * @returns {JSX.Element}
 */
export default function SiblingConfirmBar({
  target,
  busy,
  onApply,
  onDismiss
}: SiblingConfirmBarProps) {
  const count = target.ids.length;
  // Grouped digits: these counts reach five figures on a per-store submission, and
  // "18353" reads as noise where "18,353" reads as a quantity.
  const formattedCount = count.toLocaleString();
  const metric = target.metricName ?? 'the confirmed metric';
  return (
    // Snackbar docks with `position: fixed` and does NOT portal on its own, so any
    // ancestor carrying a transform/filter/will-change would become its containing
    // block and strand it mid-page. The Portal puts it on document.body, where
    // "fixed" can only mean the viewport.
    <Portal>
      <Snackbar
        open
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        // Wide enough that the message and both actions stay on one line: a long
        // label plus a five-figure count wrapped the button onto two lines at 880.
        sx={{ maxWidth: 'calc(100% - 48px)', width: 'min(1320px, calc(100% - 48px))' }}
      >
        <Alert
          severity="info"
          sx={{ width: '100%', boxShadow: 6 }}
          action={
            <Stack direction="row" spacing={1} alignItems="center">
              <GhostButton dark outlined onClick={onDismiss} disabled={busy}>
                Dismiss
              </GhostButton>
              <PrimaryButton
                onClick={onApply}
                disabled={busy}
                loading={busy}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Apply to all {formattedCount}
              </PrimaryButton>
            </Stack>
          }
        >
          {formattedCount} more row{count === 1 ? '' : 's'} in this file{' '}
          {count === 1 ? 'is' : 'are'} labelled &ldquo;{target.rawLabel}&rdquo; and can take{' '}
          {metric}.
        </Alert>
      </Snackbar>
    </Portal>
  );
}
